import { createApi } from "@reduxjs/toolkit/query/react";
import fetchBaseQueryCSRF from "../helper";

/**
 * `geotargets` are criteria_id values. The server intersects them with the
 * workspace configuration and resolves them into location names itself, so the
 * front never has to know which mart column they land in.
 */
export interface LocalQueryArgs {
    workspaceId?: number;
    startDate: string | null;
    endDate: string | null;
    compareStartDate?: string | null;
    compareEndDate?: string | null;
    countries?: string[];
    devices?: string[];
    platforms?: string[];
    keywords?: string[];
    geotargets?: number[];
}

// Visibility API (ads)
const visibility = createApi({
    baseQuery: fetchBaseQueryCSRF({ baseUrl: process.env.CROSS_API_URL }),
    reducerPath: "api.visibility",
    tagTypes: ["visibility"],
    endpoints: (builder) => ({

        // All ads data
        getAdsData: builder.query<any, {
            startDate: string | null,
            endDate: string | null,
            device: string[] | null,
            countries: string[],
            keywords: string[],
            tags?: number | string[]
            workspaceTable?: any,
            workspaceId?: any,
            domain?: string,
            platform: any
        }>({
            query: ({ startDate, endDate, device, countries, keywords, workspaceTable, workspaceId, domain, platform, tags }) => {
                const url = `/visibility/ads/getAdsData`;
                const body = {
                    startDate,
                    endDate,
                    device,
                    countries,
                    keywords,
                    workspaceTable,
                    workspaceId,
                    domain,
                    tags,
                    platform,
                };
                return {
                    url,
                    method: "POST",
                    body: JSON.stringify(body),
                    headers: {
                        "Content-Type": "application/json",
                        "Cache-Control": "no-cache",
                        Accept: "application/json",
                    },
                };
            },
        }),

        // Keywords with paid presence rate for a selected domain
        getKeywordPresence: builder.query<any, {
            startDate: string | null;
            endDate: string | null;

            compare?: boolean;
            compareStartDate?: string | null;
            compareEndDate?: string | null;

            device: string[] | null;
            countries: string[];
            domain: string;
            workspaceTable?: any;
            platform?: any;
        }>({
            query: ({
                startDate,
                endDate,
                compare,
                compareStartDate,
                compareEndDate,
                device,
                countries,
                domain,
                workspaceTable,
                platform,
            }) => ({
                url: `/visibility/ads/getKeywordPresence`,
                method: "POST",
                body: JSON.stringify({
                    startDate,
                    endDate,
                    compare,
                    compareStartDate,
                    compareEndDate,
                    device,
                    countries,
                    domain,
                    workspaceTable,
                    platform,
                }),
                headers: {
                    "Content-Type": "application/json",
                    "Cache-Control": "no-cache",
                    Accept: "application/json",
                },
            }),
        }),

        // Side queries (top competitors global + by ad when adType provided)
        getSideQueries: builder.query<any, {
            startDate: string | null,
            endDate: string | null,

            compare?: boolean,
            compareStartDate?: string | null,
            compareEndDate?: string | null,

            device: string[] | null,
            tags?: number | string,
            countries: string[],
            keywords: string[],
            workspaceTable?: any,
            workspaceId?: any,
            domain?: string,
            platform: any,
            adType?: string[]
        }>({
            query: ({
                startDate,
                endDate,
                compare,
                compareStartDate,
                compareEndDate,
                device,
                countries,
                keywords,
                workspaceTable,
                workspaceId,
                domain,
                platform,
                adType,
                tags,
            }) => {
                const url = `/visibility/ads/getSideQueries`;
                const body = {
                    startDate,
                    endDate,
                    compare,
                    compareStartDate,
                    compareEndDate,
                    device,
                    countries,
                    keywords,
                    workspaceTable,
                    workspaceId,
                    domain,
                    platform,
                    tags,
                    adType,
                };

                return {
                    url,
                    method: "POST",
                    body: JSON.stringify(body),
                    headers: {
                        "Content-Type": "application/json",
                        "Cache-Control": "no-cache",
                        Accept: "application/json",
                    },
                };
            },
        }),

        // Visibility > Local grid: one row per top level location, carrying both
        // the selected window and the comparison window.
        getLocalKpis: builder.query<any, LocalQueryArgs>({
            query: (args) => ({
                url: `/visibility/local/kpis`,
                method: "POST",
                body: JSON.stringify(args),
                headers: {
                    "Content-Type": "application/json",
                    "Cache-Control": "no-cache",
                    Accept: "application/json",
                },
            }),
        }),
    }),
});

export const {
    useGetAdsDataQuery,
    useGetSideQueriesQuery,
    useGetKeywordPresenceQuery,
    useGetLocalKpisQuery,
} = visibility;

export default visibility;


